import { buildDatasetSidebarItems } from '@/app/layouts/datasetSidebarItems';
import { ROUTE_PREFETCH_MAP } from '@/app/router/routePrefetch';
import type { SideBarItem } from '@/app/shell/SideBar';
import { isDiagnosticEnabled, isReportsEnabled } from '@/shared/config/featureFlags';
import { useTrans } from '@/shared/hooks/useTranslation';
import { useCreditsNavViewModel } from '@/viewmodel/features/credits';
import { useStudioAvailability, useStudioSectionOpen } from '@/viewmodel/features/dashboard';
import { useDataShareCount } from '@/viewmodel/features/dataShare';
import { useSourceCountViewModel } from '@/viewmodel/features/etlSource';
import {
  useCurrentProjectIdViewModel,
  usePermissionsViewModel,
} from '@/viewmodel/features/project';
import { useIsStaffViewModel } from '@/viewmodel/features/user';
import { gateByPermission, useSideBarNavViewModel } from '@/viewmodel/shared/sidebar';

export function useDatasetSidebar(): {
  visibleItems: SideBarItem[];
  activeItem: string | undefined;
  activeLabel: string | undefined;
  openItem: (item: SideBarItem) => void;
} {
  const { t } = useTrans();
  const { can } = usePermissionsViewModel();
  const credits = useCreditsNavViewModel();
  const isStaff = useIsStaffViewModel();

  const projectId = useCurrentProjectIdViewModel();
  const showPresentations = useStudioAvailability(projectId);
  const videosOpen = useStudioSectionOpen('videos');
  const sourceCount = useSourceCountViewModel();
  const shareCount = useDataShareCount(projectId);

  const sideBarItems = buildDatasetSidebarItems(t, {
    sourceCount,
    shareCount,
    showDiagnostic: isDiagnosticEnabled() && isStaff,
    showReports: isReportsEnabled(),
    showPresentations,
    showVideos: showPresentations && videosOpen,
    credits,
  });
  const prefetchableItems = sideBarItems.map((item) => ({
    ...item,
    prefetch: item.path ? ROUTE_PREFETCH_MAP[item.path] : undefined,
  }));
  const visibleItems = gateByPermission(prefetchableItems, can, t('common.roleRestricted'));
  const { activeKey, activeLabel, open } = useSideBarNavViewModel(visibleItems);

  return { visibleItems, activeItem: activeKey, activeLabel, openItem: open };
}
